import { formatLocalDate } from '@/utils/localDateUtils';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Inmueble, InmuebleEstado } from '@/types';
import inmuebleService from '@/services/inmuebleService';
import DataTable, { Column } from '@/components/common/DataTable';
import MainLayout from '@/components/layout/MainLayout';
import { getClientFullName } from '@/utils/clientUtils';
import { useAuth } from '@/contexts/AuthContext';

export default function PropertyList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const [result, setResult] = useState<{ items: Inmueble[]; total: number; page: number; totalPages: number }>({
    items: [],
    total: 0,
    page: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const skipSearchEffect = useRef(true);

  const load = async (p = 1, searchTerm = search) => {
    setLoading(true);
    try {
      const ownerId = searchParams.get('owner_id') || searchParams.get('propietario_id');
      const filters = ownerId
        ? { owner_id: Number(ownerId), page: p, limit: 25, search: searchTerm.trim() || undefined }
        : { page: p, limit: 25, search: searchTerm.trim() || undefined };
      const res: any = await inmuebleService.getInmuebles(filters);
      setResult(res.data);
      setPage(res.data.page);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, search);
  }, [searchParams]);

  useEffect(() => {
    if (skipSearchEffect.current) {
      skipSearchEffect.current = false;
      return;
    }

    const timeout = window.setTimeout(() => load(1, search), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return formatLocalDate(dateString, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const columns: Column<Inmueble>[] = [
    {
      key: 'id',
      label: 'N°',
      sortable: true,
      render: (property) => `#${property.id}`
    },
    {
      key: 'direccion',
      label: 'Dirección',
      sortable: true,
    },
    {
      key: 'monto_alquiler',
      label: 'Importe del Alquiler',
      render: (property) => {
        if (!property.contrato_activo?.monto_alquiler_propietario) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <span className="font-medium">
            {property.contrato_activo.moneda_propietario}{' '}
            {Number(property.contrato_activo.monto_alquiler_propietario).toLocaleString('es-UY')}
          </span>
        );
      }
    },
    {
      key: 'numero_contrato',
      label: 'N° Contrato',
      render: (property) => property.contrato_activo?.numero_contrato || '-'
    },
    {
      key: 'inquilinos',
      label: 'Inquilinos',
      className: 'text-center',
      render: (property) => {
        if (!property.contrato_activo?.inquilino_nombre) return '-';
        return (
          <div className="flex justify-center text-center text-xs">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/clients/${property.contrato_activo!.inquilino_id}`);
              }}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              {property.contrato_activo.inquilino_nombre}
            </button>
          </div>
        );
      }
    },
    {
      key: 'fecha_contrato',
      label: 'Fecha Contrato',
      className: 'hidden 2xl:table-cell',
      render: (property) => property.contrato_activo ? formatDate(property.contrato_activo.fecha_inicio) : '-'
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      render: (property) => {
        const statusColors: Record<string, string> = {
          [InmuebleEstado.LIBRE]: 'bg-green-100 text-green-800',
          [InmuebleEstado.ALQUILADO]: 'bg-blue-100 text-blue-800'
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            statusColors[property.estado] || 'bg-gray-100 text-gray-800'
          }`}>
            {property.estado}
          </span>
        );
      }
    },
    {
      key: 'propietarios',
      label: 'Propietarios',
      className: 'hidden 2xl:table-cell',
      render: (property) => {
        if (!property.propietarios || property.propietarios.length === 0) return '-';
        const propietarios = property.propietarios;
        return (
          <div className="flex flex-wrap justify-center gap-1 text-center text-xs">
            {propietarios.map((p, index) => (
              <span key={p.id}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/clients/${p.id}`);
                  }}
                  className="text-center text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {getClientFullName(p)}
                </button>
                {index < propietarios.length - 1 && <span className="text-gray-400">; </span>}
              </span>
            ))}
          </div>
        );
      }
    }
  ];

  const handleRowClick = (property: Inmueble) => {
    navigate(`/properties/${property.id}`);
  };

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inmuebles</h1>
            <p className="text-gray-600 mt-1">Gestión de propiedades</p>
          </div>
          {hasPermission('properties', 'write') && (
            <button
              onClick={() => navigate('/properties/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nuevo Inmueble
            </button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={result.items}
          loading={loading}
          searchable={true}
          serverSearch={true}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Buscar por N°, dirección, ciudad, contrato, inquilino..."
          searchFields={['id', 'direccion', 'ciudad']}
          onRowClick={handleRowClick}
          rowKey="id"
          emptyState={
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏠</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay inmuebles registrados</h3>
              <p className="text-gray-600 mb-4">Comienza agregando tu primera propiedad</p>
              {hasPermission('properties', 'write') && (
                <button
                  onClick={() => navigate('/properties/new')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Agregar Inmueble
                </button>
              )}
            </div>
          }
        />

        {result.items.length > 0 && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <div className="text-sm text-gray-600">
              Mostrando {((page - 1) * 25) + 1} a {Math.min(page * 25, result.total)} de {result.total} inmuebles
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page <= 1}
                onClick={() => load(page - 1, search)}
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-gray-700">
                Página {page} de {result.totalPages}
              </span>
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page >= result.totalPages}
                onClick={() => load(page + 1, search)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
